// @ts-strict-ignore
import {AfterViewInit, Component, ElementRef, Injector, ViewChild} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {SimMaterialDialogService, NotificationService, SimUploadService, SimUtilityService} from "@services";
import {ApiError, MediaType, UploadMediaErrorCode} from "@models";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {animate, query, sequence, stagger, style, transition, trigger} from "@angular/animations";
import {HttpEventType} from "@angular/common/http";
import {DomSanitizer} from "@angular/platform-browser";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";

export interface DialogData_UploadFileDialogInput extends MatDialogDataBaseInput {}

export interface DialogData_UploadFileDialogOutput extends MatDialogDataBaseOutput {
	uploadedFiles?: boolean;
}

@Component({
	selector: "sim-upload-file-dialog",
	templateUrl: "./upload-file-dialog.component.html",
	styleUrls: ["./upload-file-dialog.component.scss"],
	animations: [
		zoomInOnEnterAnimation({duration: 175, delay: 0}),
		zoomOutOnLeaveAnimation({duration: 175, delay: 0}),
		trigger("listStagger", [
			transition("* <=> *", [
				query(
					":enter",
					[
						style({opacity: 0, transform: "translateY(-15px)"}),
						stagger(
							"50ms",
							animate(
								"550ms ease-out",
								style({
									opacity: 1,
									transform: "translateY(0px)",
								}),
							),
						),
					],
					{optional: true},
				),
				query(":leave", animate("200ms", style({opacity: 0})), {
					optional: true,
				}),
			]),
		]),
		trigger("anim", [
			transition("* => void", [
				style({
					height: "*",
					opacity: "1",
					transform: "translateX(0)",
					"box-shadow": "none",
				}),
				sequence([
					animate(
						".25s ease",
						style({
							height: "*",
							opacity: ".2",
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
					animate(
						".1s ease",
						style({
							height: "0",
							opacity: 0,
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
				]),
			]),
			transition("void => active", [
				style({
					height: "*",
					opacity: "0",
					transform: "translateX(30px)",
					"box-shadow": "none",
				}),
				sequence([
					animate(
						".1s ease",
						style({
							height: "*",
							opacity: ".2",
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
					animate(
						".25s ease",
						style({
							height: "*",
							opacity: 1,
							transform: "translateX(0)",
							"box-shadow": "none",
						}),
					),
				]),
			]),
		]),
	],
	standalone: false,
})
export class UploadFileDialogComponent
	extends BasicMaterialDialog<UploadFileDialogComponent, DialogData_UploadFileDialogInput, DialogData_UploadFileDialogOutput>
	implements AfterViewInit
{
	MediaType = MediaType;
	UploadMediaErrorCode = UploadMediaErrorCode;
	mediaTypeKey: string = "Files";
	SimUtilityService = SimUtilityService;
	afterViewInit: boolean;
	@ViewChild("uploadInput", {static: false}) uploadInput: ElementRef;
	@ViewChild("folderUploadInput", {static: false})
	folderUploadInput: ElementRef;
	currentSelectedFiles: (File & {
		mediaUrl?: string;
		newName?: string;
		progress?: number;
		isPlaying?: boolean;
		progressUploading?: number;
		isUploading?: boolean;
	})[];
	acceptedFileTypes: string = "*";
	acceptedFileTypesText: string = "*";
	@ViewChild("audioPlayer") audioPlayerRef: ElementRef<HTMLAudioElement>;
	public currentFileIndex: number = -1;
	public uploadingMediaFiles: boolean;
	public uploadedFiles: boolean = false;
	public type: MediaType;
	public replaceAllFlag: boolean = false;
	public replaceMode: boolean = false;

	constructor(
		private _simNotificationService: NotificationService,
		private _simUploadService: SimUploadService,
		private domSanitizer: DomSanitizer,
		private _translate: TranslateService,
		private _simDialogService: SimMaterialDialogService,
		_injector: Injector,
	) {
		super(_injector);
	}

	ngOnInit(): void {}

	ngAfterViewInit() {
		this.setupForMediaType();
	}

	public setupForMediaType() {
		this.afterViewInit = true;
		this.acceptedFileTypes = "mp3, wav, mp4, png, jpg, jpeg, pdf, pptx";
		this.acceptedFileTypesText =
			"audio/mp3, audio/mpeg, audio/wav, video/mp4, image/png, image/jpg, image/jpeg, application/pdf, application/pptx";
	}

	chooseFiles() {
		this.acceptedFileTypes = "mp3, wav, mp4, png, jpg, jpeg, pdf, pptx";
		if (!this.currentSelectedFiles) {
			this.uploadInput.nativeElement.click();
		} else {
			if (this.currentFileIndex != -1) {
				this.audioPlayerRef.nativeElement.pause();
				this.currentSelectedFiles[this.currentFileIndex].isPlaying = false;
				this.currentSelectedFiles[this.currentFileIndex].progress = 0;
			}
			this.currentSelectedFiles = undefined;
			this.uploadInput.nativeElement.value = "";
			this.uploadInput.nativeElement.click();
		}
	}

	chooseFolders() {
		this.acceptedFileTypes = "folder";
		if (!this.currentSelectedFiles) {
			this.folderUploadInput.nativeElement.click();
		} else {
			if (this.currentFileIndex != -1) {
				this.audioPlayerRef.nativeElement.pause();
				this.currentSelectedFiles[this.currentFileIndex].isPlaying = false;
				this.currentSelectedFiles[this.currentFileIndex].progress = 0;
			}
			this.currentSelectedFiles = undefined;
			this.folderUploadInput.nativeElement.value = "";
			this.folderUploadInput.nativeElement.click();
		}
	}
	fileTypeProcessing(file): boolean {
		const fileType = file.type;
		if (this.acceptedFileTypesText.includes(fileType)) {
			if (fileType === "audio/mp3" || fileType === "audio/wav") {
				this.type = MediaType.Audio;
			} else if (fileType === "video/mp4") {
				this.type = MediaType.Video;
			} else if (fileType === "image/png" || fileType === "image/jpg" || fileType === "image/jpeg") {
				this.type = MediaType.Image;
			} else if (fileType === "application/pdf") {
				this.type = MediaType.PDF;
			} else if (fileType === "application/pptx") {
				this.type = MediaType.PowerPoint;
			} else {
				this.type = MediaType.Undefined;
			}
			return true;
		} else {
			this.type = MediaType.Undefined;
			return false;
		}
	}

	handleFileInput(event: Event) {
		const files = (event.target as HTMLInputElement).files;

		if (files?.length === 0) {
			this._simNotificationService.notifyInfo(this._translate.instant(Translations.UPLOAD_MEDIA_DIALOG_NOTIFYERROR));
			this.currentSelectedFiles = undefined;
			return;
		}

		this.currentSelectedFiles = Array.from(files);
		this.currentSelectedFiles.forEach((value) => {
			if (this.fileTypeProcessing(value) && this.type === MediaType.Video) {
				value.mediaUrl = <any>this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(value));
			} else {
				value.mediaUrl = URL.createObjectURL(value);
			}
		});
	}

	handleFolderInput(event: Event) {
		const inputElement = event.target as HTMLInputElement;
		const files = inputElement.files;

		if (files?.length === 0) {
			this._simNotificationService.notifyInfo(this._translate.instant(Translations.UPLOAD_MEDIA_DIALOG_NOTIFYERROR));
			this.currentSelectedFiles = undefined;
			return;
		}

		this.currentSelectedFiles = [];
		const filesArray = Array.from(files);

		filesArray.forEach((file) => {
			if (this.acceptedFileTypesText.includes(file.type)) {
				this.currentSelectedFiles.push(file);
			}
		});

		this.currentSelectedFiles.forEach((value) => {
			if (this.fileTypeProcessing(value) && this.type === MediaType.Video) {
				value.mediaUrl = <any>this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(value));
			} else {
				value.mediaUrl = URL.createObjectURL(value);
			}
		});
	}
	timeUpdateAudio($event: Event) {
		const progress = (this.audioPlayerRef.nativeElement.currentTime / this.audioPlayerRef.nativeElement.duration) * 100;
		if (this.currentSelectedFiles[this.currentFileIndex]) {
			this.currentSelectedFiles[this.currentFileIndex].progress = progress;
		}
		if (progress === 100) {
			this.currentSelectedFiles[this.currentFileIndex].isPlaying = false;
			this.currentSelectedFiles[this.currentFileIndex].progress = 0;
			this.audioPlayerRef.nativeElement.currentTime = 0;
		}
	}

	toggleMediaPlaying(file, index: number) {
		if (this.currentFileIndex != -1 && this.currentFileIndex !== index) {
			this.audioPlayerRef.nativeElement.pause();
			this.currentSelectedFiles[this.currentFileIndex].isPlaying = false;
			this.currentSelectedFiles[this.currentFileIndex].progress = 0;
		}

		if (file.isPlaying) {
			this.audioPlayerRef.nativeElement.pause();
			file.isPlaying = false;
			file.progress = 0;
			this.currentFileIndex = -1;
		} else {
			this.audioPlayerRef.nativeElement.src = file.mediaUrl;
			this.currentFileIndex = index;
			this.audioPlayerRef.nativeElement.play().then(
				() => {
					file.isPlaying = true;
				},
				(err) => {
					this._simNotificationService.notifyError(
						this._translate.instant(Translations.UPLOAD_MEDIA_DIALOG_NOTIFYERROR_MEDIA_FILE),
					);
					file.isPlaying = false;
				},
			);
		}
	}

	deleteFromSelection(i: number) {
		if (this.currentFileIndex === i) {
			this.audioPlayerRef.nativeElement.pause();
			this.currentSelectedFiles[this.currentFileIndex].isPlaying = false;
			this.currentSelectedFiles[this.currentFileIndex].progress = 0;
			this.currentFileIndex = -1;
		}

		this.currentSelectedFiles.splice(i, 1);
	}

	uploadFiles() {
		this.uploadedFiles = true;
		this.uploadingMediaFiles = true;
		this.replaceAllFlag = false;
		this.replaceMode = false;

		this.currentSelectedFiles.forEach(async (file) => {
			file.progressUploading = 0;
			file.isUploading = true;

			let result = await this._simUploadService.checkUploadMedia({
				fileName: file.name,
				description: file.newName || file.name,
				category: "",
			});
			if (result?.success) {
				await this._simUploadService
					.uploadMedia({
						uploadedFile: file,
						description: file.newName || file.name,
						category: "",
					})
					.subscribe({
						next: (response) => {
							if (response.type === HttpEventType.Response) {
								if (response.body.success) {
									this._simNotificationService.notifySuccess();

									setTimeout(() => {
										if (file.progressUploading === 100) {
											this.currentSelectedFiles.forEach((item, index, array) => {
												if (item === file) {
													array.splice(index, 1);
												}
											});
										}
									}, 200);
								} else {
									this._simNotificationService.notifyError(response.body.reason);
								}

								file.isUploading = false;
							}

							if (response.type === HttpEventType.UploadProgress) {
								file.progressUploading = Math.round((100 * response.loaded) / response.total);
							}
						},
						error: (err: ApiError) => {
							if (!err.wasLogged) {
								this._simNotificationService.notifyError("Could not upload file!");
							}
						},
					});
			} else if (!result?.success && result.errorCode === UploadMediaErrorCode.DuplicateFileName && !this.replaceMode) {
				this.replaceMode = true;
				if (!this.replaceAllFlag) {
					const confirmationResult = await this._simDialogService.openReplaceDialog({
						title: this._translate.instant(this.T.UPLOAD_MEDIA_FILE_NAME_DUPLICATE),
						description: file.name + this._translate.instant(this.T.UPLOAD_MEDIA_FILE_NAME_DUPLICATE_ALERT_TO_REPLACE),
						cancelText: this._translate.instant(this.T.UPLOAD_MEDIA_FILE_NAME_DUPLICATE_ALERT_TO_REPLACE_CANCEL),
						okText: this._translate.instant(this.T.UPLOAD_MEDIA_FILE_NAME_DUPLICATE_ALERT_TO_REPLACE_REPLACE),
						replaceAllText: this._translate.instant(this.T.UPLOAD_MEDIA_FILE_NAME_DUPLICATE_ALERT_TO_REPLACE_REPLACE_ALL),
					});

					if (confirmationResult.action === "replaceAll") {
						this.replaceAllFlag = true;
						this.currentSelectedFiles.forEach((value) => {
							this.replacingMedia(value);
						});
					}
					if (confirmationResult.action === "ok") {
						this.replaceAllFlag = false;
						this.replacingMedia(file);
						this.currentSelectedFiles.forEach((item, index, array) => {
							if (item === file) {
								array.splice(index, 1);
							}
						});
						this.uploadFiles();
					}
				}
			} else if (!result?.success && this.replaceMode) {
			} else {
				this._simNotificationService.notifyError(result?.reason ?? "Could not upload file!");
			}
		});
		console.log("FINISHED UPLOADING...");
	}

	private replacingMedia(file) {
		this._simUploadService
			.replaceMedia({
				uploadedFile: file,
				description: file.newName || file.name,
				category: "",
			})
			.subscribe({
				next: (response) => {
					if (response.type === HttpEventType.Response) {
						if (response.body.success) {
							this._simNotificationService.notifySuccess();
							this.currentSelectedFiles.forEach((item, index, array) => {
								if (item === file) {
									array.splice(index, 1);
								}
							});
						} else {
							this._simNotificationService.notifyError(response.body.reason);
						}
						file.isUploading = false;
					}

					if (response.type === HttpEventType.UploadProgress) {
						file.progressUploading = Math.round((100 * response.loaded) / response.total);
					}
				},
				error: (err: ApiError) => {
					if (!err.wasLogged) {
						this._simNotificationService.notifyError("Could not upload file!");
					}
				},
			});
	}
}
